import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isAfter } from "date-fns";

import { createReplaceAllReducer } from "store/utils";
import { HomeRole } from "utils/urls";

export const LOCAL_STATE_KEY = "monarch-local";

export type AutoMuteScenario =
  | "initial_join"
  | "refresh"
  | "breakout_listening"
  | "breakout_return";

export type LocalAvState = {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  preferredMicDeviceLabel?: string;
  preferredCameraDeviceLabel?: string;
  isBackgroundBlurEnabled: boolean;
  wasAutoMuted: boolean;
  lastAutoMuteScenario?: AutoMuteScenario;
};

export type UserAgentInfo = {
  isBrowserAvCompatible: boolean;
  isDesktopDevice: boolean;
  browserName: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: string;
};

export type LocalState = {
  isSidebarOpen: boolean;
  editMode: boolean;
  /* scroll positions for persistent scroll containers */
  scrollPositions: { [key: string]: number };
  localAvState: LocalAvState;
  /* datetime of the last read message in class chat */
  classChatReadAt: { [sectionId: string]: string };
  /* the last home view role we viewed the home page as */
  lastHomeRole: HomeRole | null;
  /* the last view option we viewed the sections list as */
  lastSectionViewOption: "course" | "term" | null;
  /* last API error encountered */
  apiError: { model: string; action: string; errorMessage: string } | null;
  userAgentInfo: UserAgentInfo | null;
};

const defaultState: LocalState = {
  isSidebarOpen: true,
  editMode: false,
  scrollPositions: {},
  localAvState: {
    isMicEnabled: true,
    isCameraEnabled: true,
    isBackgroundBlurEnabled: false,
    wasAutoMuted: false,
  },
  classChatReadAt: {},
  lastHomeRole: null,
  lastSectionViewOption: "course",
  apiError: null,
  userAgentInfo: null,
};

export const retrieveLocalState = () => {
  const serializedState = localStorage.getItem(LOCAL_STATE_KEY);
  const storedState = (serializedState ? JSON.parse(serializedState) : {}) as Record<
    string,
    unknown
  >;
  const typedStoredState = storedState as Partial<LocalState>;
  return {
    ...defaultState,
    ...typedStoredState,
  };
};

const saveLocalState = (state: LocalState) => {
  localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify(state));
};

const deleteLocalState = () => {
  localStorage.removeItem(LOCAL_STATE_KEY);
};

const initialState = retrieveLocalState;

// TODO: add "saveLocalState" as a middleware for all reducers in this slice
export const LocalStore = createSlice({
  name: "local",
  initialState,
  reducers: {
    setIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
      saveLocalState(state);
    },
    setIsEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
      saveLocalState(state);
    },
    setLastHomeRole: (state, action: PayloadAction<HomeRole | null>) => {
      state.lastHomeRole = action.payload;
      saveLocalState(state);
    },
    setLastSectionViewOption: (state, action: PayloadAction<"course" | "term" | null>) => {
      state.lastSectionViewOption = action.payload;
      saveLocalState(state);
    },
    setScrollPosition: (state, action: PayloadAction<{ id: string; position: number }>) => {
      state.scrollPositions[action.payload.id] = action.payload.position;
      saveLocalState(state);
    },
    setLocalMicIsEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isMicEnabled = action.payload;
      saveLocalState(state);
    },
    setLocalCameraIsEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isCameraEnabled = action.payload;
      saveLocalState(state);
    },
    setPreferredMicDeviceLabel: (state, action: PayloadAction<string | undefined>) => {
      state.localAvState.preferredMicDeviceLabel = action.payload;
      saveLocalState(state);
    },
    setPreferredCameraDeviceLabel: (state, action: PayloadAction<string | undefined>) => {
      state.localAvState.preferredCameraDeviceLabel = action.payload;
      saveLocalState(state);
    },
    setIsBackgroundBlurEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isBackgroundBlurEnabled = action.payload;
      saveLocalState(state);
    },
    setWasAutoMuted: (state, action: PayloadAction<boolean>) => {
      state.localAvState.wasAutoMuted = action.payload;
      saveLocalState(state);
    },
    setLastAutoMuteScenario: (state, action: PayloadAction<AutoMuteScenario | undefined>) => {
      state.localAvState.lastAutoMuteScenario = action.payload;
      saveLocalState(state);
    },
    updateClassChatMessagesReadAt: (
      state,
      action: PayloadAction<{ sectionId: string; readAt: string }>,
    ) => {
      const { sectionId, readAt } = action.payload;
      const lastReadAt = state.classChatReadAt[sectionId];
      if (!lastReadAt || isAfter(readAt, lastReadAt)) {
        state.classChatReadAt[sectionId] = readAt;
        saveLocalState(state);
      }
    },
    setApiError: (
      state,
      action: PayloadAction<{ model: string; action: string; errorMessage: string } | null>,
    ) => {
      state.apiError = action.payload;
      saveLocalState(state);
    },
    setUserAgentInfo: (state, action: PayloadAction<UserAgentInfo>) => {
      state.userAgentInfo = action.payload;
      saveLocalState(state);
    },
  },
  extraReducers: createReplaceAllReducer<LocalState>("local", initialState, () => {
    deleteLocalState();
  }),
});

export const localStateActions = LocalStore.actions;
